/**
 * Per-site WordPress Detail Page — /wordpress/sites/:siteId
 *
 * Reached by clicking a site name on the WP Beacon Dashboard. Shows a
 * single site's full operational surface without the multi-site Sites
 * table / picker chrome.
 *
 * Tabs: Content · Plugins · Themes · Updates · Comments · Users · Site settings
 *
 * All tab components are re-used from WordPressPage.js so behaviour /
 * fallbacks (BeaconOnlyNotice, snapshot-first Themes, etc.) stay in one
 * place.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  ArrowLeft, Loader2, PanelsTopLeft, ExternalLink,
  Signal, SignalHigh, SignalMedium, SignalLow,
} from "lucide-react";
import {
  ContentTab, PluginsTab, ThemesTab, UpdatesTab,
  CommentsTab, UsersTab, WpSettingsTab,
} from "@/pages/WordPressPage";

function beaconBadge(site) {
  const cfg = site?.detected?.beacon ? {} : { label: "no beacon", tone: "zinc" };
  const lastSeen = site?.beacon_last_seen_at
    ? new Date(site.beacon_last_seen_at).getTime() : null;
  if (!site?.detected?.beacon) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-zinc-100 text-zinc-500 border-zinc-200">
        <Signal className="h-3 w-3 mr-1" /> no beacon
      </Badge>
    );
  }
  if (!lastSeen) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-zinc-100 text-zinc-500 border-zinc-200">
        <Signal className="h-3 w-3 mr-1" /> configured
      </Badge>
    );
  }
  const mins = Math.round((Date.now() - lastSeen) / 60000);
  if (mins < 30) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
        <SignalHigh className="h-3 w-3 mr-1" /> fresh · {mins}m ago
      </Badge>
    );
  }
  if (mins < 24 * 60) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-amber-50 text-amber-800 border-amber-200">
        <SignalMedium className="h-3 w-3 mr-1" /> stale · {Math.round(mins / 60)}h ago
      </Badge>
    );
  }
  return (
    <Badge variant="outline"
      className="rounded-sm text-[10px] bg-red-50 text-red-700 border-red-200">
      <SignalLow className="h-3 w-3 mr-1" /> offline · {Math.round(mins / 60 / 24)}d ago
    </Badge>
  );
}

export default function WpSiteDetailPage() {
  const { siteId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "content";
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      // /wp/sites returns every site — cheapest way to hydrate a single
      // one without a dedicated endpoint.
      const { data } = await api.get("/wp/sites");
      const s = (data || []).find((x) => x.site_id === siteId);
      if (!s) { setSite(null); setNotFound(true); }
      else { setSite(s); setNotFound(false); }
    } catch {
      setSite(null); setNotFound(true);
    } finally { setLoading(false); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="wp-site-detail-loading">
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading site…
        </CardContent></Card>
      </div>
    );
  }
  if (notFound || !site) {
    return (
      <div className="space-y-4" data-testid="wp-site-detail-not-found">
        <Link to="/wp-beacon" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-[#002FA7]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Beacon Dashboard
        </Link>
        <Card className="rounded-sm">
          <CardContent className="p-8 text-center text-zinc-500 text-sm">
            Site not found or you do not have access.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wp-site-detail-page">
      {/* Back nav */}
      <Link to="/wp-beacon"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-[#002FA7]"
        data-testid="wp-site-detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Beacon Dashboard
      </Link>

      {/* Site identity card */}
      <Card className="rounded-sm border-l-4 border-l-[#002FA7]">
        <CardContent className="p-5 flex items-start gap-4">
          <PanelsTopLeft className="h-6 w-6 text-[#002FA7] shrink-0 mt-1" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-bold text-xl leading-tight"
                data-testid="wp-site-detail-name">{site.name}</h1>
              {beaconBadge(site)}
              {site.detected?.wp_version && (
                <Badge variant="outline"
                  className="rounded-sm text-[10px] bg-zinc-50 text-zinc-600 border-zinc-200">
                  WP {site.detected.wp_version}
                </Badge>
              )}
              {site.detected?.plugin_updates_count > 0 && (
                <Badge variant="outline"
                  className="rounded-sm text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                  {site.detected.plugin_updates_count} plugin update{site.detected.plugin_updates_count === 1 ? "" : "s"}
                </Badge>
              )}
              <Badge variant="outline"
                className={`rounded-sm text-[10px] ${
                  site.status === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : site.status === "auth_failed" ? "bg-amber-50 text-amber-800 border-amber-200"
                    : "bg-zinc-100 text-zinc-500 border-zinc-200"
                }`}>
                {site.status || "unchecked"}
              </Badge>
            </div>
            <a href={site.url} target="_blank" rel="noreferrer"
              className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-[#002FA7]">
              {site.url} <ExternalLink className="h-3 w-3" />
            </a>
            <div className="text-[11px] text-zinc-400 mt-1 flex flex-wrap items-center gap-3">
              <span>App-pw: {site.has_wp_auth ? "yes" : "no"}</span>
              <span>Woo: {site.has_woo_keys ? "keys stored" : "no keys"}</span>
              {site.detected?.php_version && <span>PHP {site.detected.php_version}</span>}
              {site.beacon_last_seen_at && (
                <span title={site.beacon_last_seen_at}>
                  beacon last poll: {new Date(site.beacon_last_seen_at).toLocaleString("en-AU",
                    { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Operational tabs — same components as the WordPress API page */}
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="content"      data-testid="wp-site-tab-content">Content</TabsTrigger>
          <TabsTrigger value="plugins"      data-testid="wp-site-tab-plugins">Plugins</TabsTrigger>
          <TabsTrigger value="themes"       data-testid="wp-site-tab-themes">Themes</TabsTrigger>
          <TabsTrigger value="updates"      data-testid="wp-site-tab-updates">Updates</TabsTrigger>
          <TabsTrigger value="comments"     data-testid="wp-site-tab-comments">Comments</TabsTrigger>
          <TabsTrigger value="users"        data-testid="wp-site-tab-users">Users</TabsTrigger>
          <TabsTrigger value="wp-settings"  data-testid="wp-site-tab-settings">Site settings</TabsTrigger>
        </TabsList>
        <TabsContent value="content"     className="mt-3"><ContentTab     siteId={siteId} /></TabsContent>
        <TabsContent value="plugins"     className="mt-3"><PluginsTab     siteId={siteId} /></TabsContent>
        <TabsContent value="themes"      className="mt-3"><ThemesTab      siteId={siteId} /></TabsContent>
        <TabsContent value="updates"     className="mt-3"><UpdatesTab     siteId={siteId} /></TabsContent>
        <TabsContent value="comments"    className="mt-3"><CommentsTab    siteId={siteId} /></TabsContent>
        <TabsContent value="users"       className="mt-3"><UsersTab       siteId={siteId} /></TabsContent>
        <TabsContent value="wp-settings" className="mt-3"><WpSettingsTab  siteId={siteId} /></TabsContent>
      </Tabs>
    </div>
  );
}
